// Payment Status Enum
export const PAYMENT_STATUS = {
  TO_BE_ISSUED: 'TO_BE_ISSUED',
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
  OVERDUE: 'OVERDUE' // Display-only status calculated from due date
};

// Payment Category Enum
export const PAYMENT_CATEGORY = {
  INVOICE: 'INVOICE',
  FREIGHT_FEES: 'FREIGHT_FEES',
  TAX_DUTIES: 'TAX_DUTIES'
};

// Payment Category Labels
export const PAYMENT_CATEGORY_LABELS = {
  [PAYMENT_CATEGORY.INVOICE]: 'Invoice',
  [PAYMENT_CATEGORY.FREIGHT_FEES]: 'Freight Fees',
  [PAYMENT_CATEGORY.TAX_DUTIES]: 'Tax & Duties'
};

// Payment Category Config (for chips/badges)
export const PAYMENT_CATEGORY_CONFIG = {
  [PAYMENT_CATEGORY.INVOICE]: {
    label: 'Invoice',
    color: 'primary',
    icon: '📄'
  },
  [PAYMENT_CATEGORY.FREIGHT_FEES]: {
    label: 'Freight',
    color: 'info',
    icon: '🚢'
  },
  [PAYMENT_CATEGORY.TAX_DUTIES]: {
    label: 'Tax & Duties',
    color: 'warning',
    icon: '📋'
  }
};

// Payment category options for forms
export const PAYMENT_CATEGORY_OPTIONS = [
  { value: null, label: 'Uncategorized' },
  ...Object.entries(PAYMENT_CATEGORY_LABELS).map(([value, label]) => ({ value, label }))
];

// Payment Status Labels
export const PAYMENT_STATUS_LABELS = {
  [PAYMENT_STATUS.TO_BE_ISSUED]: 'To Be Issued',
  [PAYMENT_STATUS.AWAITING_PAYMENT]: 'Awaiting Payment',
  [PAYMENT_STATUS.PAID]: 'Paid',
  [PAYMENT_STATUS.CANCELLED]: 'Cancelled',
  [PAYMENT_STATUS.OVERDUE]: 'Overdue'
};

// Payment status options (used for forms and display)
export const PAYMENT_STATUS_OPTIONS = Object.entries(PAYMENT_STATUS_LABELS)
  .filter(([key]) => key !== PAYMENT_STATUS.OVERDUE) // Exclude OVERDUE from form options
  .map(([value, label]) => ({ value, label }));

// Payment statuses that count as "paid" for calculations
export const PAID_STATUSES = [PAYMENT_STATUS.PAID];

// Payment-specific color constants
export const PAYMENT_DUE_DATE_COLORS = {
  OVERDUE: '#DC2626',       // Red-600 - Payment is overdue
  URGENT: '#D97706',        // Amber-600 - Due today or in 1-3 days
  NEUTRAL: 'text.primary'   // Use theme's text color for all other cases
};

// Map for display labels of late payment indicators
export const LATE_PAYMENT_LABELS = {
  PAID_ON_TIME: 'On Time',
  PAID_LATE: 'Paid Late'
};

// File Upload Configuration
export const FILE_UPLOAD_CONFIG = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB in bytes
  MAX_SIZE_MB: 10, // For display purposes
  ALLOWED_MIME_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png',
    'image/jpeg',
    'image/jpg'
  ],
  ALLOWED_EXTENSIONS: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg'],
  ALLOWED_FORMATS_DISPLAY: 'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG'
};

// Date Validation Constants
export const DATE_VALIDATION = {
  MIN_YEAR: 1900,
  MAX_YEAR: 2100,
  DATE_FORMAT: 'YYYY-MM-DD',
  BRAZILIAN_DATE_FORMAT: 'DD/MM/YYYY',
  BRAZILIAN_DATE_REGEX: /^\d{4}-\d{2}-\d{2}$/
};

// API Endpoints
export const PAYMENT_ENDPOINTS = {
  BASE: '/payment',
  LIST: '/payment/',
  NEXT_PAYMENTS: '/payment/next_payments/',
  BY_STATUS: (status) => `/payment/?status=${status}`,
  BY_ORDER: (orderId) => `/payment/?sales_order=${orderId}`,
  OVERDUE: '/payment/?overdue=true',
  PAID_LATE: '/payment/?paid_late=true',
  GENERATE: '/payment/generate',
  RECALCULATE: '/payment/recalculate',
  DASHBOARD: '/payment/dashboard/',
  OVERRIDE_VALUES: (id) => `/payment/${id}/override_values/`,
  CHECK_STALENESS: (orderId) => `/payment/check-staleness/${orderId}/`,
};

// --- Value Override ---

// Statuses where the override button is blocked
export const OVERRIDE_BLOCKED_STATUSES = [
  PAYMENT_STATUS.PAID,
  PAYMENT_STATUS.CANCELLED,
];

// Confirmation dialog copy, keyed by current payment status
const OVERRIDE_COMMON_BULLETS = [
  'USD, BRL, and Exchange Rate will become editable',
  'Percentage will remain read-only (template property)',
  'Future recalculations will not change these amounts',
];

export const OVERRIDE_DIALOG_MESSAGES = {
  [PAYMENT_STATUS.TO_BE_ISSUED]: {
    title: 'Edit payment amounts manually?',
    bullets: OVERRIDE_COMMON_BULLETS,
    statusChangeBullet: "This payment's status will change to Awaiting Payment",
  },
  [PAYMENT_STATUS.AWAITING_PAYMENT]: {
    title: 'Edit payment amounts manually?',
    bullets: OVERRIDE_COMMON_BULLETS,
    statusChangeBullet: null,
  },
};

// Tooltip messages for disabled override button states
export const OVERRIDE_TOOLTIPS = {
  [PAYMENT_STATUS.PAID]: 'Paid payments cannot be modified',
  [PAYMENT_STATUS.CANCELLED]: 'Cancelled payments cannot be modified',
  LAST_INVOICE: 'This is the last template component \u2014 its value is automatically calculated as the remaining balance to ensure the total matches the invoice',
};

// Default Form Values
export const DEFAULT_PAYMENT_FORM = {
  contract_number: '',
  intermediary: '',
  date: '',
  due_date: '',
  brl_value: '',
  usd_value: '',
  dollar_exchange: '',
  status: PAYMENT_STATUS.TO_BE_ISSUED,
  percentage: '',
  category: null, // Payment category
  is_estimated_date: false,
  file: null
};

// UI Messages
export const PAYMENT_MESSAGES = {
  CREATE_SUCCESS: 'Payment created successfully!',
  UPDATE_SUCCESS: 'Payment updated successfully!',
  DELETE_SUCCESS: 'Payment deleted successfully!',
  CREATE_ERROR: 'Failed to create payment',
  UPDATE_ERROR: 'Failed to update payment',
  DELETE_ERROR: 'Failed to delete payment',
  FILE_SIZE_ERROR: `File size must be less than ${FILE_UPLOAD_CONFIG.MAX_SIZE_MB}MB`,
  FILE_TYPE_ERROR: `Invalid file type. Allowed types: ${FILE_UPLOAD_CONFIG.ALLOWED_EXTENSIONS.join(', ')}`,
  NO_FILE_SELECTED: 'No file selected',
  FILE_UPLOAD_HELP: `Allowed formats: ${FILE_UPLOAD_CONFIG.ALLOWED_FORMATS_DISPLAY} (Max: ${FILE_UPLOAD_CONFIG.MAX_SIZE_MB}MB)`,
  GENERATE_BTN: 'Calculate Payments',
  RECALCULATE_BTN: 'Recalculate Payments',
  PROCESSING: 'Processing...',
  NO_TEMPLATE: 'Order has no payment term template',
  GENERATE_SUCCESS: 'Payments generated successfully',
  RECALCULATE_SUCCESS: 'Payments recalculated successfully',
  GENERATE_ERROR: 'Failed to generate payments',
  RECALCULATE_ERROR: 'Failed to recalculate payments',
  OVERRIDE_SUCCESS: 'Values unlocked for manual editing',
  OVERRIDE_ERROR: 'Failed to override payment values',
  STALE_DIALOG_TITLE: 'Invoice Total Changed',
  STALE_DIALOG_SUBTITLE: 'Payment amounts may need to be recalculated.',
  STALE_DIALOG_CONFIRM: 'Recalculate Payments',
  STALE_DIALOG_DISMISS: 'Not Now',
};

// Dashboard - Aging period color scheme
export const AGING_COLORS = {
  '1_30': { bg: 'rgba(254, 243, 199, 0.5)', border: '#FCD34D', text: '#D97706' },
  '31_60': { bg: 'rgba(254, 215, 170, 0.5)', border: '#FDBA74', text: '#EA580C' },
  '61_90': { bg: 'rgba(254, 202, 202, 0.5)', border: '#FCA5A5', text: '#DC2626' },
  '90_plus': { bg: 'rgba(248, 113, 113, 0.5)', border: '#F87171', text: '#B91C1C' },
};

// Dashboard - Forecast period color scheme
export const FORECAST_COLORS = {
  'next_30': { bg: 'rgba(187, 247, 208, 0.5)', border: '#86EFAC', text: '#16A34A' },
  '31_60': { bg: 'rgba(191, 219, 254, 0.5)', border: '#93C5FD', text: '#2563EB' },
  '61_90': { bg: 'rgba(221, 214, 254, 0.5)', border: '#C4B5FD', text: '#7C3AED' },
  '90_plus': { bg: 'rgba(209, 213, 219, 0.5)', border: '#9CA3AF', text: '#4B5563' },
};

// Dashboard - Order statuses excluded from payment metrics by default.
// Any status NOT in this list is auto-selected when fetched from the API.
export const DEFAULT_UNSELECTED_ORDER_STATUSES = [
  'Open',
  'Discontinued',
  'Ready for Processing',
  'Awaiting Approval',
];

// Shared warning dialog styles
export const WARNING_DIALOG_STYLES = {
  ICON_COLOR: '#f59e0b',
  BOX_BG: '#fffbeb',
  BOX_BORDER: '4px solid #f59e0b',
  TEXT_COLOR: '#b45309',
  FOOTER_BG: '#f9fafb',
  FOOTER_BORDER: '1px solid #f0f0f0',
};

// Shared info/advisory dialog styles (blue tone)
export const INFO_DIALOG_STYLES = {
  HEADER_BG: '#f0f7ff',
  HEADER_BORDER: '#e0ecf8',
  ICON_COLOR: '#1565c0',
  CARD_BORDER: '#c2d9f0',
  CARD_BG: '#f0f7ff',
  FOOTER_BG: '#f8fafc',
  DELTA_INCREASE_COLOR: '#16a34a',
  DELTA_INCREASE_BG: '#f0fdf4',
  DELTA_DECREASE_COLOR: '#dc2626',
  DELTA_DECREASE_BG: '#fef2f2',
};

/**
 * Appends the order_statuses filter param to an existing query string.
 * Returns the original params unchanged when selectedStatuses is empty.
 */
export const appendOrderStatusParams = (params, selectedStatuses) => {
  if (!selectedStatuses || selectedStatuses.length === 0) return params;
  return `${params}&order_statuses=${encodeURIComponent(selectedStatuses.join(','))}`;
};

